import { useState, useEffect, useRef, useCallback } from "react";
import { mandaraCodes, type CosmicCode } from "@/mocks/mandaraCodes";
import CosmicModal from "./CosmicModal";

interface Props {
  shopifyUrl: string;
}

export default function CosmicGrid({ shopifyUrl }: Props) {
  const [selectedCode, setSelectedCode] = useState<CosmicCode | null>(null);
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [visibleItems, setVisibleItems] = useState<Set<number>>(new Set());
  const [sectionVisible, setSectionVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  // Section fade-in
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setSectionVisible(true); },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  // Staggered item reveal
  useEffect(() => {
    if (!sectionVisible) return;
    mandaraCodes.forEach((_, i) => {
      setTimeout(() => {
        setVisibleItems((prev) => new Set([...prev, i]));
      }, i * 60 + 300);
    });
  }, [sectionVisible]);

  // Particle canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const resize = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };
    resize();
    window.addEventListener("resize", resize);

    const particles: {
      x: number; y: number; r: number;
      dx: number; dy: number; alpha: number; pulse: number;
      color: string;
    }[] = [];

    const colors = ["#d4a84c", "#c9a84c", "#e8c84a", "#f0d060"];
    for (let i = 0; i < 60; i++) {
      particles.push({
        x: Math.random() * (canvas.width || 1200),
        y: Math.random() * (canvas.height || 800),
        r: Math.random() * 1.2 + 0.2,
        dx: (Math.random() - 0.5) * 0.08,
        dy: (Math.random() - 0.5) * 0.08,
        alpha: Math.random() * 0.2 + 0.03,
        pulse: Math.random() * Math.PI * 2,
        color: colors[Math.floor(Math.random() * colors.length)],
      });
    }

    let animId: number;
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particles.forEach((p) => {
        p.pulse += 0.005;
        const a = p.alpha * (0.5 + 0.5 * Math.sin(p.pulse));
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = p.color + Math.round(a * 255).toString(16).padStart(2, "0");
        ctx.fill();
        p.x += p.dx;
        p.y += p.dy;
        if (p.x < 0 || p.x > canvas.width) p.dx *= -1;
        if (p.y < 0 || p.y > canvas.height) p.dy *= -1;
      });
      animId = requestAnimationFrame(animate);
    };
    animate();
    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener("resize", resize);
    };
  }, []);

  const handleClose = useCallback(() => setSelectedCode(null), []);

  return (
    <section
      ref={sectionRef}
      className="relative py-40 px-6 overflow-hidden bg-[#060508]"
      style={{
        opacity: sectionVisible ? 1 : 0,
        transition: "opacity 1.5s ease",
      }}
    >
      {/* Background canvas */}
      <canvas ref={canvasRef} className="absolute inset-0 w-full h-full pointer-events-none" />

      {/* Sacred geometry background */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {/* Large mandala ring */}
        <div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[900px] h-[900px] rounded-full"
          style={{
            border: "1px solid rgba(201,168,76,0.04)",
          }}
        />
        <div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] rounded-full"
          style={{
            border: "1px solid rgba(201,168,76,0.06)",
          }}
        />
        <div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] rounded-full"
          style={{
            border: "1px solid rgba(201,168,76,0.05)",
          }}
        />
        {/* Center glow */}
        <div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] rounded-full"
          style={{
            background: "radial-gradient(circle, rgba(201,168,76,0.06) 0%, transparent 70%)",
            filter: "blur(40px)",
          }}
        />
      </div>

      {/* Section header */}
      <div className="relative z-10 text-center mb-24">
        <div
          className="text-xs tracking-[0.8em] uppercase mb-8"
          style={{ color: "rgba(201,168,76,0.35)", letterSpacing: "0.8em" }}
        >
          Mandara · Kingdom of Gold
        </div>

        <h2
          className="text-4xl md:text-5xl lg:text-6xl font-light text-white mb-8 leading-tight"
          style={{
            fontFamily: "'Cormorant Garamond', serif",
            letterSpacing: "0.04em",
          }}
        >
          Choose Your Cosmic Code
        </h2>

        <div
          className="w-12 h-px mx-auto mb-8"
          style={{ background: "rgba(201,168,76,0.3)" }}
        />

        <p
          className="text-white/30 text-base max-w-md mx-auto leading-relaxed"
          style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "1.05rem", lineHeight: "2" }}
        >
          選ぶのではない。<br />
          <span style={{ color: "rgba(201,168,76,0.5)" }}>呼応するコードが、あなたを選ぶ。</span>
        </p>
      </div>

      {/* 5×5 Grid */}
      <div className="relative z-10 max-w-5xl mx-auto">
        <div className="grid grid-cols-5 gap-3 md:gap-4">
          {mandaraCodes.map((code, index) => (
            <CosmicCodeCard
              key={code.id}
              code={code}
              isVisible={visibleItems.has(index)}
              isHovered={hoveredId === code.id}
              onHover={() => setHoveredId(code.id)}
              onLeave={() => setHoveredId(null)}
              onClick={() => setSelectedCode(code)}
            />
          ))}
        </div>
      </div>

      {/* Bottom phrase */}
      <div className="relative z-10 text-center mt-24">
        <p
          className="text-xs tracking-[0.5em] uppercase"
          style={{ color: "rgba(201,168,76,0.2)" }}
        >
          内側の周波数と一致する形
        </p>
      </div>

      {/* Modal */}
      <CosmicModal
        code={selectedCode}
        shopifyUrl={shopifyUrl}
        onClose={handleClose}
      />
    </section>
  );
}

interface CardProps {
  code: CosmicCode;
  isVisible: boolean;
  isHovered: boolean;
  onHover: () => void;
  onLeave: () => void;
  onClick: () => void;
}

function CosmicCodeCard({ code, isVisible, isHovered, onHover, onLeave, onClick }: CardProps) {
  return (
    <div
      className="relative cursor-pointer"
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? "translateY(0) scale(1)" : "translateY(16px) scale(0.95)",
        transition: "opacity 0.7s ease, transform 0.7s ease",
      }}
      onMouseEnter={onHover}
      onMouseLeave={onLeave}
      onClick={onClick}
    >
      {/* Card */}
      <div
        className="relative flex flex-col items-center justify-center text-center overflow-hidden"
        style={{
          aspectRatio: "1",
          background: isHovered
            ? `linear-gradient(135deg, ${code.glowColor}, rgba(14,11,18,0.95))`
            : "linear-gradient(135deg, rgba(14,11,18,0.8), rgba(8,6,8,0.9))",
          border: `1px solid ${isHovered ? code.auraColor + "50" : code.auraColor + "15"}`,
          boxShadow: isHovered
            ? `0 0 30px ${code.glowColor}, inset 0 0 20px ${code.glowColor}`
            : "none",
          transition: "all 0.5s cubic-bezier(0.16, 1, 0.3, 1)",
          padding: "12px 8px",
        }}
      >
        {/* Corner accents */}
        {isHovered && (
          <>
            <div
              className="absolute top-0 left-0 w-3 h-3"
              style={{
                borderTop: `1px solid ${code.auraColor}60`,
                borderLeft: `1px solid ${code.auraColor}60`,
              }}
            />
            <div
              className="absolute top-0 right-0 w-3 h-3"
              style={{
                borderTop: `1px solid ${code.auraColor}60`,
                borderRight: `1px solid ${code.auraColor}60`,
              }}
            />
            <div
              className="absolute bottom-0 left-0 w-3 h-3"
              style={{
                borderBottom: `1px solid ${code.auraColor}60`,
                borderLeft: `1px solid ${code.auraColor}60`,
              }}
            />
            <div
              className="absolute bottom-0 right-0 w-3 h-3"
              style={{
                borderBottom: `1px solid ${code.auraColor}60`,
                borderRight: `1px solid ${code.auraColor}60`,
              }}
            />
          </>
        )}

        {/* Glow orb on hover */}
        {isHovered && (
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background: `radial-gradient(circle at center, ${code.glowColor} 0%, transparent 70%)`,
            }}
          />
        )}

        {/* Code number */}
        <div
          className="text-xs mb-1 leading-none"
          style={{
            color: isHovered ? `${code.auraColor}60` : "rgba(255,255,255,0.1)",
            fontSize: "0.55rem",
            letterSpacing: "0.2em",
            transition: "color 0.4s",
          }}
        >
          {String(code.number).padStart(2, "0")}
        </div>

        {/* Symbol */}
        <div
          className="leading-none mb-1"
          style={{
            fontSize: "clamp(1.2rem, 2.5vw, 1.8rem)",
            color: isHovered ? code.auraColor : `${code.auraColor}60`,
            textShadow: isHovered ? `0 0 20px ${code.glowColor}` : "none",
            transition: "all 0.4s ease",
            fontFamily: "serif",
          }}
        >
          {code.symbol}
        </div>

        {/* Name */}
        <div
          className="leading-tight"
          style={{
            fontSize: "clamp(0.5rem, 1vw, 0.65rem)",
            color: isHovered ? "rgba(255,255,255,0.7)" : "rgba(255,255,255,0.25)",
            letterSpacing: "0.05em",
            fontFamily: "'Cormorant Garamond', serif",
            transition: "color 0.4s",
          }}
        >
          {code.name}
        </div>

        {/* Hover phrase */}
        <div
          className="absolute bottom-0 left-0 right-0 text-center overflow-hidden"
          style={{
            maxHeight: isHovered ? "40px" : "0",
            opacity: isHovered ? 1 : 0,
            transition: "max-height 0.4s ease, opacity 0.4s ease",
            padding: isHovered ? "0 4px 6px" : "0",
          }}
        >
          <div
            style={{
              fontSize: "0.5rem",
              color: `${code.auraColor}70`,
              letterSpacing: "0.05em",
              fontFamily: "'Cormorant Garamond', serif",
              lineHeight: "1.3",
            }}
          >
            {code.phrase}
          </div>
        </div>
      </div>
    </div>
  );
}
